import { browser } from "$app/environment";
import { b64uDecode } from "$crypto/encryption";
import { deriveInboxId } from "$crypto/pair-key";
import { identity } from "$crypto/identity.svelte";
import { pair } from "$stores/pair.svelte";
import { profile } from "$stores/profile.svelte";
import { sanitizeHandle } from "$data/nicknames";

/**
 * Bond handshake inbox client.
 *
 * When this device finishes accepting a friend's bond code we POST a tiny
 * "I bonded with you" note to the friend's inbox. The friend's app polls
 * its inbox, derives the pair-key (their priv + my pub), bonds locally,
 * then ACKs the row by id so it disappears from the inbox.
 *
 * Privacy:
 * - The recipient is identified by `deriveInboxId(theirPubkey)` — a static
 *   HKDF over the pubkey, so the server can't reverse it without already
 *   knowing the pubkey.
 * - The sender's pubkey is sent in the clear because it's already public:
 *   the bond URL the friend pasted contains the same value.
 *
 * Polling cadence:
 * - We poll on visible / focus / drawer-open and every 15s while visible.
 * - On a successful bond we dispatch `vutwin:autobond` so the layout can
 *   render a toast.
 */

const ENDPOINT = "/api/bond/handshake";
const POLL_INTERVAL_MS = 15_000;
const STORAGE_LAST_SEEN = "vutwin.bondInbox.lastSeen";

interface ServerHandshake {
  id: number;
  senderPubkey: string;
  senderHandle: string;
  ts: number;
}

let pollTimer: ReturnType<typeof setInterval> | null = null;
let inFlight = false;

/** Read/write the highest seen ts so we don't keep re-fetching old rows. */
function getLastSeen(): number {
  if (!browser) return 0;
  try {
    const raw = localStorage.getItem(STORAGE_LAST_SEEN);
    const n = raw ? Number(raw) : 0;
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

function setLastSeen(ts: number): void {
  if (!browser) return;
  try {
    localStorage.setItem(STORAGE_LAST_SEEN, String(ts));
  } catch {
    /* ignore quota */
  }
}

/** Compute this device's inbox id, or empty string if identity isn't ready. */
function myInboxId(): string {
  if (!identity.publicKey) return "";
  try {
    return deriveInboxId(identity.publicKey);
  } catch {
    return "";
  }
}

/**
 * Announce that this device just bonded with `friendPubkeyB64`. Best-effort.
 * Failures (offline, server 500) are swallowed so the bond UX still feels
 * instant — the friend will pick up the handshake the next time both sides
 * are online together.
 */
export async function announceBond(friendPubkeyB64: string): Promise<void> {
  if (!browser) return;
  if (!identity.publicKeyB64) return;

  let recipientHash: string;
  try {
    const friendPub = b64uDecode(friendPubkeyB64);
    if (friendPub.length !== 32) return;
    recipientHash = deriveInboxId(friendPub);
  } catch {
    return;
  }

  const handle = profile.handle || "twin";

  try {
    await fetch(ENDPOINT, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        recipientHash,
        senderPubkey: identity.publicKeyB64,
        senderHandle: handle,
      }),
    });
  } catch {
    /* offline / network error — friend will get the toast on next poll */
  }
}

/**
 * Fetch + apply any pending handshakes addressed to this device.
 *
 * Side effects:
 * - For each new handshake, calls `pair.bond(senderPubkey, senderHandle)`.
 * - On a fresh bond (id wasn't already known), dispatches
 *   `vutwin:bondcelebration` with `{ handle }` so the layout can render
 *   the full-screen bond ceremony (audio + themed visual).
 * - ACKs every row we successfully processed (DELETE by ids).
 */
export async function pollInbox(): Promise<void> {
  if (!browser) return;
  if (inFlight) return;

  identity.ensureLoaded();
  pair.hydrate();
  profile.hydrate();

  const inboxId = myInboxId();
  if (!inboxId) return;

  inFlight = true;
  try {
    const since = getLastSeen();
    const url = `${ENDPOINT}?for=${inboxId}${since > 0 ? `&since=${since}` : ""}`;
    const res = await fetch(url, { cache: "no-store" });
    if (!res.ok) return;

    const data = (await res.json()) as { handshakes?: ServerHandshake[] };
    const handshakes = Array.isArray(data.handshakes) ? data.handshakes : [];
    if (handshakes.length === 0) return;

    const ackIds: number[] = [];
    let highestTs = since;

    for (const h of handshakes) {
      if (typeof h.id !== "number") continue;
      if (typeof h.ts === "number" && h.ts > highestTs) highestTs = h.ts;

      const senderPubkey =
        typeof h.senderPubkey === "string" ? h.senderPubkey : "";
      const senderHandle = sanitizeHandle(h.senderHandle || "") || "twin";
      if (!senderPubkey) {
        ackIds.push(h.id);
        continue;
      }

      // pair.bond is a no-op if the bond already exists (it refreshes the
      // bonded-at timestamp), so duplicate handshake delivery is safe.
      const isNew = !Object.values(pair.records).some(
        (r) => r.pubkeyB64 === senderPubkey,
      );
      const id = pair.bond(senderPubkey, senderHandle);
      if (id) {
        if (isNew) {
          window.dispatchEvent(
            new CustomEvent("vutwin:bondcelebration", {
              detail: { handle: senderHandle },
            }),
          );
        }
      }
      // Always ack — even if pair.bond rejected (e.g. self-bond, malformed,
      // or limit reached). Otherwise the row sits forever in the inbox.
      ackIds.push(h.id);
    }

    if (highestTs > since) setLastSeen(highestTs);

    if (ackIds.length > 0) {
      try {
        await fetch(ENDPOINT, {
          method: "DELETE",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ ids: ackIds }),
        });
      } catch {
        /* server will GC stale rows after 7d via cron */
      }
    }
  } catch {
    /* network failure — try again next tick */
  } finally {
    inFlight = false;
  }
}

/**
 * Start a periodic poll while the page is visible. Idempotent — calling
 * twice does NOT spawn extra timers. `stopInboxPolling` clears them.
 */
export function startInboxPolling(): void {
  if (!browser) return;
  if (pollTimer) return;

  // Kick off immediately so a freshly bonded friend gets their toast quickly.
  void pollInbox();

  pollTimer = setInterval(() => {
    if (
      typeof document !== "undefined" &&
      document.visibilityState !== "visible"
    )
      return;
    void pollInbox();
  }, POLL_INTERVAL_MS);
}

export function stopInboxPolling(): void {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}
